import React, { useState } from "react";
import {
  useGetReactionsQuery,
  useToggleReactionMutation,
  ReactionTargetType,
} from "../../services/rtkApi/reactionsApi";

interface ReactionBarProps {
  targetType: ReactionTargetType;
  targetId: string;
  isAuthenticated: boolean;
}

const DEFAULT_REACTIONS = ["🔥", "❤️", "👏", "😍", "🎨", "✨"];

const ReactionBar: React.FC<ReactionBarProps> = ({
  targetType,
  targetId,
  isAuthenticated,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const { data, isLoading } = useGetReactionsQuery(
    { targetType, targetId },
    { skip: !targetId }
  );
  const [toggleReaction] = useToggleReactionMutation();

  const reactions = data?.reactions || {};
  const userReactions = data?.userReactions || [];
  const allowedReactions = data?.allowedReactions || DEFAULT_REACTIONS;

  // emojis that have at least one reaction
  const activeEmojis = allowedReactions.filter(
    (emoji) => reactions[emoji] && reactions[emoji] > 0
  );
  // emojis not yet used (for the picker)
  const inactiveEmojis = allowedReactions.filter(
    (emoji) => !reactions[emoji] || reactions[emoji] === 0
  );

  const handleToggle = async (emoji: string) => {
    if (!isAuthenticated) return;
    await toggleReaction({ targetType, targetId, emoji });
    setShowPicker(false);
  };

  const totalReactions = Object.values(reactions).reduce(
    (sum, count) => sum + count,
    0
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 py-2">
        <div className="h-7 w-16 bg-gray-200 rounded-full animate-pulse" />
        <div className="h-7 w-16 bg-gray-200 rounded-full animate-pulse" />
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5 py-2 relative">
      {/* active reaction pills */}
      {activeEmojis.map((emoji) => {
        const count = reactions[emoji] || 0;
        const isUserReacted = userReactions.includes(emoji);
        return (
          <button
            key={emoji}
            onClick={() => handleToggle(emoji)}
            disabled={!isAuthenticated}
            className={`
              inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-sm
              transition-all duration-150 border
              ${
                isUserReacted
                  ? "bg-blue-50 border-blue-300 text-blue-700 shadow-sm"
                  : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"
              }
              ${isAuthenticated ? "cursor-pointer" : "cursor-default"}
            `}
            title={
              isAuthenticated
                ? isUserReacted
                  ? "Click to remove your reaction"
                  : "Click to react"
                : "Sign in to react"
            }
          >
            <span className="text-base leading-none">{emoji}</span>
            <span className="text-xs font-medium tabular-nums">{count}</span>
          </button>
        );
      })}

      {/* add reaction button */}
      {isAuthenticated && inactiveEmojis.length > 0 && (
        <div className="relative">
          <button
            onClick={() => setShowPicker(!showPicker)}
            className={`
              inline-flex items-center justify-center w-7 h-7 rounded-full
              text-gray-400 border border-dashed border-gray-300
              hover:bg-gray-100 hover:text-gray-600 hover:border-gray-400
              transition-all duration-150 cursor-pointer text-sm
            `}
            title="Add a reaction"
          >
            +
          </button>

          {/* emoji picker dropdown */}
          {showPicker && (
            <>
              {/* backdrop to close picker */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowPicker(false)}
              />
              <div
                className="
                  absolute bottom-full left-0 mb-1 z-20
                  bg-white border border-gray-200 rounded-lg shadow-lg
                  p-1.5 flex gap-1
                "
              >
                {inactiveEmojis.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => handleToggle(emoji)}
                    className="
                      w-8 h-8 flex items-center justify-center rounded
                      hover:bg-gray-100 transition-colors cursor-pointer
                      text-lg
                    "
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* total count */}
      {totalReactions > 0 && (
        <span className="text-xs text-gray-400 ml-1">
          {totalReactions} reaction{totalReactions !== 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
};

export default ReactionBar;
